import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import Navbar from "@/components/Navbar";
import { generateLocalBusinessSchema } from "@/lib/schemaGenerator";
import Footer from "@/components/Footer";
import PricingSection from "@/components/PricingSection";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Plane, Crown, Gem, Star, CheckCircle2, Car, Clock, Wine, Heart, Navigation, ArrowRight, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import servicesHero from "@/assets/services-hero.jpg";
import satAirportNight from "@/assets/sat-airport-night.jpg";
import corporateTravel from "@/assets/corporate-travel.webp";
import specialEvents from "@/assets/special-events.webp";
import vipService from "@/assets/vip-service.jpg";
import hourlyService from "@/assets/hourly-service.webp";
import wineTours from "@/assets/wine-tours.png";
import funeralServices from "@/assets/funeral-services.webp";
import pointToPoint from "@/assets/point-to-point.jpg";
import rickDifference from "@/assets/rick-difference.jpg";
import audiLuxury from "@/assets/audi-luxury.jpg";
const Services = () => {
  const location = useLocation();
  
  const schema = generateLocalBusinessSchema({
    description: "Professional black car service in San Antonio: airport transfers, corporate transportation, special events, hourly service, and wine tours. Available 24/7."
  });

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }
  }, [location]);
  const serviceIds = ["airport-transfers", "corporate-travel", "military-transportation", "special-events", "vip-services", "hourly-services", "winery-tours"];
  const services = [{
    icon: <Plane className="w-12 h-12 stroke-[1.5]" />,
    title: "Airport Transfers",
    description: "Professional black car service to and from San Antonio International Airport (SAT). Our chauffeur will track your flight and adjust pickup time accordingly. No waiting in taxi lines or dealing with rideshare apps. Flat-rate pricing starting at $75.",
    features: ["Real-time flight tracking with automatic pickup adjustments", "Meet and greet service at baggage claim", "Professional luggage assistance", "Complimentary 30-minute wait time for domestic, 60 minutes for international"],
    link: "/services/airport-transportation"
  }, {
    icon: <Building2 className="w-12 h-12 stroke-[1.5]" />,
    title: "Corporate & Business Transportation",
    description: "Impress clients and executives with our luxury Audi Q7. Perfect for airport pickups, business meetings, corporate events, and executive transportation. Professional, punctual, and discreet service for San Antonio businesses.",
    features: ["Professional, punctual service for business travelers", "Dedicated corporate account management available", "Flexible billing and invoicing options", "Multiple stops and complex itineraries accommodated"],
    link: "/services/corporate-travel"
  }, {
    icon: <Shield className="w-12 h-12 stroke-[1.5]" />,
    title: "Military & Government Transportation",
    description: "Verified JBSA base access to Lackland AFB, Fort Sam Houston, and Randolph AFB. Our credentialed chauffeurs can enter without sponsorship—perfect for BMT Graduation families, BAMC appointments, and military personnel.",
    features: ["Pre-approved access to all JBSA installations", "DBIDS credentialed, background-checked chauffeurs", "BMT Graduation ceremony transportation specialist", "Direct on-base pickup and drop-off service"],
    link: "/services/military-transportation"
  }, {
    icon: <Crown className="w-12 h-12 stroke-[1.5]" />,
    title: "Wedding & Special Events",
    description: "Arrive in style for your wedding, quinceañera, prom, or special celebration. Our pristine 2025 Audi Q7 provides the perfect backdrop for your memorable moments. Flexible packages available.",
    features: ["VIP red carpet arrivals for special occasions", "Customizable vehicle decorations", "Perfect for photography and special moments", "Flexible scheduling to match your event timeline"],
    link: "/services/wedding-transportation"
  }, {
    icon: <Clock className="w-12 h-12 stroke-[1.5]" />,
    title: "Hourly Chauffeur Service",
    description: "Need a dedicated driver for multiple stops? Book our hourly chauffeur service for wine tours, shopping trips, or business appointments throughout San Antonio and surrounding areas.",
    features: ["Flexible hourly bookings for your schedule", "Multiple stops included without additional fees", "Complimentary wait time between locations", "Ideal for wine tours, shopping, and business errands"],
    link: "/services/hourly-service"
  }, {
    icon: <Navigation className="w-12 h-12 stroke-[1.5]" />,
    title: "Point-to-Point Service",
    description: "Direct transportation from any location in San Antonio to your destination. Perfect for dinner reservations, concerts, sporting events, or medical appointments.",
    features: ["Direct transportation to any San Antonio destination", "Perfect for restaurants, concerts, and sporting events", "Medical appointment transportation", "No surge pricing - flat rates"],
    link: "/book"
  }, {
    icon: <Wine className="w-12 h-12 stroke-[1.5]" />,
    title: "Wine Tours",
    description: "Experience the beauty of Texas Hill Country and Fredericksburg wine country in luxury and comfort. Relax and enjoy the scenery while we handle the driving. Perfect for couples, small groups, and wine enthusiasts.",
    features: ["Curated wine country tours through Texas Hill Country", "Professional chauffeur knowledgeable about local wineries", "Seamless transportation between tasting rooms", "Complimentary extra time at sunset for perfect photos"],
    link: "/services/wine-tours"
  }];
  const benefits = [{
    icon: <Clock className="w-8 h-8" />,
    title: "24/7 Availability",
    description: "Round-the-clock service, 365 days a year"
  }, {
    icon: <CheckCircle2 className="w-8 h-8" />,
    title: "Professional Drivers",
    description: "Licensed, vetted, and extensively trained chauffeurs"
  }, {
    icon: <Car className="w-8 h-8" />,
    title: "Premium Fleet",
    description: "Latest model luxury vehicles, meticulously maintained"
  }];
  return <div className="min-h-screen">
      <SEO 
        title="Services | Rick's Black Car Service San Antonio"
        description="Professional black car service in San Antonio: airport transfers, corporate transportation, special events, hourly service, and wine tours. Available 24/7."
        canonicalPath="/services"
        schema={schema}
      />
      <Navbar />
      <main className="pt-20">
        <Breadcrumb 
          items={[
            { name: "Services", url: "/services" }
          ]}
        />
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${servicesHero})`
        }}>
            <div className="absolute inset-0 bg-gradient-to-r from-background/50 via-background/30 to-background/50" />
          </div>
          <div className="container mx-auto px-4 relative z-10 overflow-hidden">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">Our Services</span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground break-words">
                San Antonio Black Car Service
                <span className="block text-2xl sm:text-3xl md:text-4xl mt-2 text-primary">Luxury Transportation Services</span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
                From airport transfers and corporate transportation to special events and hourly chauffeur service - Rick's Black Car Service delivers professional, reliable luxury transportation throughout San Antonio and surrounding areas.
              </p>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto">
              {services.map((service, index) => (
                <Link key={index} to={service.link} className="block">
                  <Card id={serviceIds[index]} className="card-luxury hover:scale-105 transition-transform duration-300 scroll-mt-24 relative overflow-hidden h-full">
                    {index === 0 && (
                      <div 
                        className="absolute inset-0 bg-cover opacity-30"
                        style={{ backgroundImage: `url(${satAirportNight})`, backgroundPosition: '60% center' }}
                      />
                    )}
                    {index === 1 && (
                      <div 
                        className="absolute inset-0 bg-cover bg-center opacity-30"
                        style={{ backgroundImage: `url(${corporateTravel})` }}
                      />
                    )}
                    {index === 2 && (
                      <div 
                        className="absolute inset-0 bg-cover bg-center opacity-30"
                        style={{ backgroundImage: `url(${satAirportNight})` }}
                      />
                    )}
                    {index === 3 && (
                      <div 
                        className="absolute inset-0 bg-cover bg-center opacity-30"
                        style={{ backgroundImage: `url(${specialEvents})` }}
                      />
                    )}
                    {index === 4 && (
                      <div 
                        className="absolute inset-0 bg-cover bg-center opacity-30"
                        style={{ backgroundImage: `url(${hourlyService})` }}
                      />
                    )}
                    {index === 5 && (
                      <div 
                        className="absolute inset-0 bg-cover bg-center opacity-30"
                        style={{ backgroundImage: `url(${pointToPoint})` }}
                      />
                    )}
                    {index === 6 && (
                      <div 
                        className="absolute inset-0 bg-cover bg-center opacity-30"
                        style={{ backgroundImage: `url(${wineTours})` }}
                      />
                    )}
                    <CardContent className="p-8 space-y-6 relative z-10">
                      <div className="text-primary">{service.icon}</div>
                      <h3 className="text-2xl font-serif font-bold text-foreground">{service.title}</h3>
                      <p className="text-muted-foreground leading-relaxed text-sm">{service.description}</p>
                      <div className="space-y-3">
                        <p className="text-sm font-semibold text-foreground tracking-wider uppercase">Distinctions of Service:</p>
                        <ul className="space-y-3">
                          {service.features.map((feature, i) => <li key={i} className="flex items-start space-x-3 text-muted-foreground text-sm">
                              <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                              <span className="leading-relaxed">{feature}</span>
                            </li>)}
                        </ul>
                      </div>
                      <div className="pt-4">
                        <span className="text-primary text-sm font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                          Learn More <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Specialized Services Section */}
        <section className="py-20 bg-background">
...
        </section>

        {/* Pricing Section */}
        <PricingSection />

        {/* Benefits Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="relative w-full h-full">
              <img 
                src={rickDifference} 
                alt="Professional chauffeur - Rick's Black Car Service San Antonio" 
                className="w-full h-full object-cover opacity-90 brightness-125"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/75 to-background/80" />
            </div>
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-16">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase drop-shadow-lg">Why Choose Us</span>
              <h2 className="text-4xl md:text-5xl font-serif font-bold mt-2 text-foreground drop-shadow-lg">
                The Rick's Difference
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => <div key={index} className="text-center space-y-4">
                  <div className="flex justify-center text-primary drop-shadow-lg">{benefit.icon}</div>
                  <h3 className="text-xl font-semibold text-foreground drop-shadow-md">{benefit.title}</h3>
                  <p className="text-foreground/90 drop-shadow-sm">{benefit.description}</p>
                </div>)}
            </div>
          </div>
        </section>

        {/* Blog Section */}
        <section className="py-20 bg-muted/20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-4">
                Learn More From Our Blog
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Helpful guides and insights about luxury transportation services in San Antonio
              </p>
            </div>
            <div className="max-w-4xl mx-auto">
              <Card className="card-luxury">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-foreground">
                        Transportation Guides
                      </h3>
                      <ul className="space-y-2 text-muted-foreground">
                        <li>• <Link to="/blog" className="hover:text-primary transition-colors">Airport transfer tips and best practices</Link></li>
                        <li>• <Link to="/blog" className="hover:text-primary transition-colors">Corporate travel etiquette and planning</Link></li>
                        <li>• <Link to="/blog" className="hover:text-primary transition-colors">Wedding transportation coordination</Link></li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-foreground">
                        Local Insights
                      </h3>
                      <ul className="space-y-2 text-muted-foreground">
                        <li>• <Link to="/blog" className="hover:text-primary transition-colors">Texas Hill Country wine tour guides</Link></li>
                        <li>• <Link to="/blog" className="hover:text-primary transition-colors">San Antonio event transportation tips</Link></li>
                        <li>• <Link to="/blog" className="hover:text-primary transition-colors">Holiday travel and seasonal advice</Link></li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-8 text-center">
                    <Button asChild className="btn-luxury">
                      <Link to="/blog">
                        Explore All Articles <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="relative w-full h-full">
              <img 
                src={audiLuxury} 
                alt="Luxury car interior - Rick's Black Car Service" 
                className="w-full h-full object-cover opacity-90 brightness-125"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/75 via-background/80 to-background/85" />
            </div>
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <Card className="card-luxury bg-card/40 backdrop-blur-sm border-primary/20">
              <CardContent className="p-12 text-center space-y-6">
                <h2 className="text-4xl font-serif font-bold text-foreground drop-shadow-lg">
                  Ready to Experience Luxury?
                </h2>
                <p className="text-foreground/90 text-lg max-w-2xl mx-auto drop-shadow-md">
                  Contact us today to discuss your transportation needs and receive a personalized quote.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button className="btn-luxury text-lg px-8 py-6">Request a Quote</Button>
                  <Button asChild variant="outline" className="text-lg px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                    <a href="tel:210-296-7409">Call Us Now</a>
                  </Button>
                  <Button asChild className="text-lg px-8 py-6 bg-blue-400 hover:bg-blue-500 text-white">
                    <a href="sms:210-296-7409">Text us Now</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>
      <Footer />
    </div>;
};
export default Services;